import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, ChevronDown, Loader2, Star } from 'lucide-react';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import API_BASE from '../../utils/apiBase';

const LIMIT_LABELS = [
  ['admin_users_limit', 'Staff seats'],
  ['workers_limit', 'Workers'],
  ['service_users_limit', 'Service users'],
  ['client_organisations_limit', 'Client organisations'],
  ['applicant_invites_monthly_limit', 'Applicant invitations/month'],
  ['storage_gb_limit', 'Storage GB'],
];

function formatPence(amount, suffix = '') {
  if (typeof amount !== 'number') return 'Contact CareOps';
  const formatted = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount / 100);
  return `${formatted}${suffix}`;
}

function formatLimit(value) {
  if (!value) return 'Not included';
  return Number(value).toLocaleString();
}

function stateBadge(state) {
  const tone = {
    Included: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    'Available as add-on': 'border-blue-200 bg-blue-50 text-blue-700',
    Beta: 'border-amber-200 bg-amber-50 text-amber-700',
    'Coming soon': 'border-slate-200 bg-slate-50 text-slate-600',
  }[state] || 'border-slate-200 bg-white text-slate-500';
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>{state}</span>;
}

export default function PricingPage() {
  const [catalogue, setCatalogue] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [expanded, setExpanded] = useState({ 'Workforce & Recruitment': true });
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/public/commercial-catalogue`)
      .then((response) => setCatalogue(response.data))
      .catch(() => setError('Pricing is temporarily unavailable.'));
  }, []);

  const plans = catalogue?.plans || [];
  const categories = catalogue?.categories || [];
  const comparison = catalogue?.comparison || {};

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <Header />
      <main>
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">CareOps plans</h1>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                Transparent CareOps packaging for workforce, compliance, care delivery and commercial growth. Prices exclude VAT.
              </p>
            </div>
            <div className="mt-6">
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" aria-label="Billing interval">
                {['monthly', 'annual'].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`rounded-md px-4 py-2 text-sm font-medium capitalize ${billingInterval === value ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                    onClick={() => setBillingInterval(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-sm text-slate-500">{catalogue?.annual_saving_note || 'Annual pricing includes two months compared with monthly billing.'}</p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          {!catalogue && !error ? (
            <div className="flex min-h-[260px] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : null}
          {error ? <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{error}</div> : null}

          {catalogue ? (
            <div className="space-y-8">
              <div className="grid gap-5 lg:grid-cols-3">
                {plans.map((plan) => {
                  const amount = billingInterval === 'annual' ? plan.annual_amount : plan.monthly_amount;
                  const cta = plan.id === 'enterprise' ? 'Talk to CareOps' : plan.id === 'professional' ? 'Choose Professional' : 'Start with CareOps';
                  return (
                    <div key={plan.id} className={`relative rounded-lg border bg-white p-6 shadow-sm ${plan.most_popular ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200'}`}>
                      {plan.most_popular ? <Badge className="absolute right-5 top-5 bg-primary text-white"><Star className="mr-1 h-3 w-3" />Most popular</Badge> : null}
                      <p className="text-sm font-semibold text-primary">{plan.positioning}</p>
                      <h2 className="mt-2 text-2xl font-bold text-slate-950">{plan.name}</h2>
                      <p className="mt-3 min-h-[64px] text-sm leading-relaxed text-slate-600">{plan.description}</p>
                      <p className="mt-5 text-3xl font-semibold text-slate-950">
                        {plan.enterprise_from_price && billingInterval === 'monthly' ? 'From ' : ''}{formatPence(amount, billingInterval === 'annual' ? '/year' : '/month')}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Excluding VAT</p>
                      <Button asChild className="mt-5 w-full bg-primary text-white hover:bg-primary-hover">
                        <Link to="/contact?type=platform_demo">{cta}</Link>
                      </Button>
                      <div className="mt-5 space-y-2">
                        {LIMIT_LABELS.map(([key, label]) => (
                          <div key={key} className="flex justify-between gap-3 text-sm">
                            <span className="text-slate-600">{label}</span>
                            <span className="font-medium text-slate-900">{formatLimit(plan.limits?.[key])}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <section className="rounded-lg border border-slate-200 bg-white p-6">
                <h2 className="text-xl font-semibold text-slate-950">Add-ons</h2>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {catalogue.addons.map((addon) => (
                    <div key={addon.id} className="rounded-lg border border-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">{addon.name}</p>
                          <p className="mt-1 text-sm text-slate-600">{formatPence(addon.price_monthly, '/month')}</p>
                        </div>
                        {stateBadge(addon.status === 'coming_soon' ? 'Coming soon' : addon.status === 'beta' ? 'Beta' : 'Available as add-on')}
                      </div>
                      <p className="mt-3 text-sm text-slate-600">Contact CareOps</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-lg border border-slate-200 bg-white p-6">
                <h2 className="text-xl font-semibold text-slate-950">Full comparison</h2>
                <div className="mt-4 space-y-3">
                  {categories.map((category) => (
                    <div key={category} className="rounded-lg border border-slate-200">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-4 py-3 text-left font-semibold text-slate-900"
                        onClick={() => setExpanded((current) => ({ ...current, [category]: !current[category] }))}
                      >
                        {category}
                        <ChevronDown className={`h-4 w-4 transition-transform ${expanded[category] ? 'rotate-180' : ''}`} />
                      </button>
                      {expanded[category] ? (
                        <div className="overflow-x-auto border-t border-slate-200">
                          <table className="w-full min-w-[760px] text-sm">
                            <thead>
                              <tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                                <th className="px-4 py-2">Feature</th>
                                {plans.map((plan) => <th key={plan.id} className="px-4 py-2">{plan.name}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {(comparison[category] || []).map((feature) => (
                                <tr key={feature.id} className="border-t border-slate-100">
                                  <td className="px-4 py-3 font-medium text-slate-800">
                                    <span className="inline-flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" />{feature.name}</span>
                                  </td>
                                  {plans.map((plan) => <td key={plan.id} className="px-4 py-3">{stateBadge(feature.states?.[plan.id])}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>
            </div>
          ) : null}
        </section>
      </main>
      <Footer />
    </div>
  );
}
